import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Badge, 
  ListGroup, 
  Form, 
  Spinner, 
  Alert, 
  Modal, 
  Table, 
  Tabs, 
  Tab, 
  ProgressBar, 
  InputGroup 
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaChalkboardTeacher, 
  FaGraduationCap, 
  FaBook, 
  FaUsers, 
  FaUserGraduate, 
  FaClock, 
  FaChartLine, 
  FaEye, 
  FaPlus, 
  FaEdit, 
  FaSearch, 
  FaKey, 
  FaVideo, 
  FaMoneyBill, 
  FaStar, 
  FaExclamationTriangle 
} from 'react-icons/fa';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip as ChartTooltip, 
  Legend as ChartLegend, 
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { 
  User, 
  Course, 
  StudentProgress, 
  TeacherDashboardStats
} from '../../types';
import { 
  getTeacherDashboardStats, 
  getCoursesByTeacherId, 
  getLessonEngagement, 
  generateCourseCode, 
  getCourseCodes, 
  updateCodeExpiryDate, 
  getStudentDetails, 
  addStudentNote, 
  getStudentNote, 
  getTeacherStudents 
} from '../../services/teacherService';
import { 
  formatTime, 
  formatDate 
} from '../../utils/formatters';
import CourseCodesManager from '../../components/teacher/CourseCodesManager';
import CourseCodesReports from '../../components/teacher/CourseCodesReports';
import './TeacherDashboard.css';

// تسجيل مكونات Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler
);

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    name: string;
  }>;
  label?: string;
}

interface ChartDataEntry {
  name: string;
  value: number;
}

const TeacherDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // State declarations
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [courses, setCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<TeacherDashboardStats | null>(null);
  const [lessonEngagement, setLessonEngagement] = useState<any[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [showCodesReports, setShowCodesReports] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCourseForCodes, setSelectedCourseForCodes] = useState<string | null>(null);
  const [studentNote, setStudentNote] = useState('');

  const handleStudentClick = async (studentId: string) => {
    try {
      if (!currentUser) return;
      const details = await getStudentDetails(studentId, currentUser.uid);
      if (details) {
        setSelectedStudent(details);
        const noteData = await getStudentNote(studentId, currentUser.uid);
        setStudentNote(noteData ? noteData.note : '');
        setShowStudentModal(true);
      }
    } catch (err) {
      setError('حدث خطأ أثناء تحميل بيانات الطالب');
      console.error(err);
    }
  };

  const handleSaveNote = async () => {
    try {
      if (!currentUser || !selectedStudent) return;
      await addStudentNote(selectedStudent.user.id, currentUser.uid, studentNote);
      setShowStudentModal(false);
    } catch (err) {
      setError('حدث خطأ أثناء حفظ الملاحظة');
      console.error(err);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} ساعة ${minutes} دقيقة`;
  };

  const renderStatCard = (icon: React.ReactNode, title: string, value: string | number, color: string) => (
    <Card className="stat-card mb-3">
      <Card.Body>
        <div className="d-flex align-items-center">
          <div className={`stat-icon ${color}`}>{icon}</div>
          <div className="ms-3">
            <h6 className="mb-0">{title}</h6>
            <h3 className="mb-0">{value}</h3>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const renderCourseAnalytics = () => {
    if (!selectedCourse || !stats) return null;

    const course = courses.find(c => c.id === selectedCourse);
    if (!course) return null;

    const studentProgress = stats.topPerformingStudents
      .concat(stats.lowEngagementStudents)
      .filter(student => student.courseId === selectedCourse)
      .map(student => ({
        name: student.userName,
        progress: student.progress || 0,
        watchTime: student.watchTime || 0,
        completedLessons: student.completedLessons || 0,
        totalLessons: student.totalLessons || 0
      }));

    const averageProgress = stats.progressPerCourse[selectedCourse] || 0;
    const totalStudents = stats.studentsPerCourse[selectedCourse] || 0;

    return (
      <div className="mt-4">
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">تحليلات الكورس: {course.title}</h5>
            <Badge bg="primary">{totalStudents} طالب</Badge>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <h6 className="mb-3">توزيع تقدم الطلاب</h6>
                    <div className="chart-placeholder" />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <h6 className="mb-3">إكمال الدروس</h6>
                    <div className="chart-placeholder" />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Card>
                  <Card.Body>
                    <h6 className="mb-3">وقت المشاهدة لكل طالب</h6>
                    <div className="chart-placeholder" />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col md={4}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="mb-0">{Math.round(averageProgress)}%</h3>
                    <p className="text-muted mb-0">متوسط تقدم الطلاب</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="mb-0">{totalStudents}</h3>
                    <p className="text-muted mb-0">عدد الطلاب المسجلين</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="mb-0">{formatTime(stats.totalWatchTime || 0)}</h3>
                    <p className="text-muted mb-0">إجمالي وقت المشاهدة</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderCourseSelector = () => (
    <div className="mb-4">
      <Form.Group>
        <Form.Label>اختر الكورس لعرض التحليلات التفصيلية</Form.Label>
        <Form.Select
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
        >
          <option value="">اختر كورساً...</option>
          {courses.map(course => (
            <option key={course.id} value={course.id}>{course.title}</option>
          ))}
        </Form.Select>
      </Form.Group>
    </div>
  );

  const renderOverview = () => (
    <>
      <Row>
        <Col md={3}>
          {renderStatCard(
            <FaUsers />,
            'إجمالي الطلاب',
            stats?.totalStudents || 0,
            'bg-primary'
          )}
        </Col>
        <Col md={3}>
          {renderStatCard(
            <FaBook />,
            'الكورسات',
            stats?.totalCourses || 0,
            'bg-success'
          )}
        </Col>
        <Col md={3}>
          {renderStatCard(
            <FaVideo />,
            'الدروس',
            stats?.totalLessons || 0,
            'bg-info'
          )}
        </Col>
        <Col md={3}>
          {renderStatCard(
            <FaMoneyBill />,
            'الإيرادات',
            `${stats?.totalRevenue || 0} جنيه`,
            'bg-warning'
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">نصيب الكورسات</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-placeholder" />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">توزيع الطلاب على الكورسات</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-placeholder" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {renderCourseSelector()}
      {selectedCourse && renderCourseAnalytics()}

      <Row className="mt-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">أعلى الدروس مشاهدة</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-placeholder" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">أعلى الدروس مشاهدة</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-placeholder" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          {renderLessonEngagementChart()}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">آخر الاشتراكات</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>الطالب</th>
                    <th>الكورس</th>
                    <th>تاريخ الاشتراك</th>
                    <th>التقدم</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recentEnrollments.map(enrollment => (
                    <tr key={enrollment.id}>
                      <td>{(enrollment as any).studentName || 'طالب'}</td>
                      <td>{enrollment.courseName}</td>
                      <td>
                        {formatDate(enrollment.enrollmentDate)}
                      </td>
                      <td>
                        <ProgressBar
                          now={enrollment.progress || 0}
                          label={`${Math.round(enrollment.progress || 0)}%`}
                          variant={getProgressVariant(enrollment.progress || 0)}
                        />
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleStudentClick(enrollment.userId)}
                        >
                          <FaEye /> عرض التفاصيل
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderOverviewStats = () => {
    if (!stats) return null;

    return (
      <Row className="stats-cards mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">{stats.totalStudents}</h3>
                  <p className="text-muted mb-0">إجمالي الطلاب</p>
                </div>
                <div className="stats-icon bg-primary">
                  <FaUsers />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">{stats.totalCourses}</h3>
                  <p className="text-muted mb-0">إجمالي الكورسات</p>
                </div>
                <div className="stats-icon bg-success">
                  <FaBook />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">{stats.totalLessons}</h3>
                  <p className="text-muted mb-0">إجمالي الدروس</p>
                </div>
                <div className="stats-icon bg-info">
                  <FaVideo />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">{formatTime(stats.totalWatchTime || 0)}</h3>
                  <p className="text-muted mb-0">إجمالي وقت المشاهدة</p>
                </div>
                <div className="stats-icon bg-warning">
                  <FaClock />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderTopStudents = () => {
    if (!stats?.topPerformingStudents?.length) return null;

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">أفضل الطلاب أداءً</h5>
          <Badge bg="success">متميزون</Badge>
        </Card.Header>
        <Card.Body>
          <ListGroup variant="flush">
            {stats.topPerformingStudents.slice(0, 5).map((student, index) => (
              <ListGroup.Item key={`top-student-${student.userId}-${index}`} className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <div className="student-rank me-3">{index + 1}</div>
                  <div>
                    <h6 className="mb-0">{student.userName}</h6>
                    <small className="text-muted">{student.courseName}</small>
                  </div>
                </div>
                <div className="text-end">
                  <div>
                    <Badge bg="success">{Math.round(student.progress)}%</Badge>
                  </div>
                  <small>{formatTime(student.watchTime)}</small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    );
  };

  const renderLowEngagementStudents = () => {
    if (!stats?.lowEngagementStudents?.length) return null;

    return (
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">الطلاب الأقل مشاركة</h5>
          <Badge bg="warning" text="dark">يحتاجون للمتابعة</Badge>
        </Card.Header>
        <Card.Body>
          <ListGroup variant="flush">
            {stats.lowEngagementStudents.slice(0, 5).map((student, index) => (
              <ListGroup.Item key={`low-student-${student.userId}-${index}`} className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <div>
                    <h6 className="mb-0">{student.userName}</h6>
                    <small className="text-muted">{student.courseName}</small>
                  </div>
                </div>
                <div className="text-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleStudentClick(student.userId)}
                  >
                    <FaEye /> عرض التفاصيل
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    );
  };

  const renderRecentEnrollments = () => {
    if (!stats?.recentEnrollments?.length) return null;

    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">آخر الاشتراكات</h5>
        </Card.Header>
        <Card.Body>
          <ListGroup variant="flush">
            {stats.recentEnrollments.slice(0, 5).map((enrollment, index) => (
              <ListGroup.Item
                key={`enrollment-${enrollment.id || index}`}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <h6 className="mb-0">{(enrollment as any).studentName || 'طالب'}</h6>
                  <small>{enrollment.courseName}</small>
                </div>
                <div>
                  {formatDate(enrollment.enrollmentDate)}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    );
  };

  const renderDashboardContent = () => {
    if (loading) {
      return (
        <Container className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">جاري تحميل البيانات...</p>
        </Container>
      );
    }

    if (error) {
      return (
        <Container className="py-5">
          <Alert variant="danger">{error}</Alert>
        </Container>
      );
    }

    if (!stats) {
      return (
        <Container className="py-5">
          <Alert variant="info">
            لا توجد بيانات لعرضها. قم بإضافة كورسات وطلاب لعرض الإحصائيات.
          </Alert>
        </Container>
      );
    }

    return (
      <Container fluid className="py-4">
        {renderOverviewStats()}

        <Row>
          <Col md={6}>
            {renderTopStudents()}
          </Col>
          <Col md={6}>
            {renderLowEngagementStudents()}
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            {renderRecentEnrollments()}
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">الكورسات الخاصة بك</h5>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => navigate('/teacher/courses/new')}
                >
                  <FaPlus className="me-1" /> إضافة كورس جديد
                </Button>
              </Card.Header>
              <Card.Body>
                {courses.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>اسم الكورس</th>
                        <th>عدد الطلاب</th>
                        <th>نسبة التقدم</th>
                        <th>عدد الدروس</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map(course => {
                        const courseId = course.id;
                        return (
                          <tr key={courseId}>
                            <td>{course.title}</td>
                            <td>{stats.studentsPerCourse[courseId] || 0}</td>
                            <td>
                              <ProgressBar 
                                now={stats?.progressPerCourse?.[courseId] || 0} 
                                label={`${Math.round(stats?.progressPerCourse?.[courseId] || 0)}%`}
                                variant={getProgressVariant(stats?.progressPerCourse?.[courseId] || 0)}
                              />
                            </td>
                            <td>{course.lessonsCount || 0}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => navigate(`/teacher/courses/${courseId}`)}
                              >
                                <FaEye /> عرض
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="me-2"
                                onClick={() => navigate(`/teacher/courses/${courseId}/edit`)}
                              >
                                <FaEdit /> تعديل
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => {
                                  setSelectedCourseForCodes(courseId);
                                  setShowCodesModal(true);
                                }}
                              >
                                <FaKey /> الأكواد
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info">
                    لا توجد كورسات خاصة بك. قم بإضافة كورس جديد لعرض الإحصائيات.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  };

  const renderStudentModal = () => (
    <Modal show={showStudentModal} onHide={() => setShowStudentModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedStudent?.user.name} - تفاصيل الطالب
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedStudent && (
          <>
            <Row>
              <Col md={4}>
                <div className="text-center mb-3">
                  {selectedStudent.user.photoURL ? (
                    <img
                      src={selectedStudent.user.photoURL}
                      alt={selectedStudent.user.name}
                      className="rounded-circle mb-2"
                      width="100"
                      height="100"
                    />
                  ) : (
                    <div className="avatar-placeholder-large mb-2">
                      {selectedStudent.user.name[0]}
                    </div>
                  )}
                  <h5>{selectedStudent.user.name}</h5>
                  <p className="text-muted">{selectedStudent.user.email}</p>
                </div>
              </Col>
              <Col md={8}>
                <Card className="mb-3">
                  <Card.Body>
                    <h6>إحصائيات عامة</h6>
                    <Row>
                      <Col md={6}>
                        <p>
                          <strong>متوسط التقدم:</strong>{' '}
                          {Math.round(selectedStudent.averageProgress || 0)}%
                        </p>
                        <p>
                          <strong>وقت المشاهدة:</strong>{' '}
                          {formatTime(selectedStudent.totalWatchTime || 0)}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p>
                          <strong>درجة المشاركة:</strong>{' '}
                          {Math.round((selectedStudent.engagementScore || 0) * 10)}/100
                        </p>
                        <p>
                          <strong>آخر نشاط:</strong>{' '}
                          {formatDate(selectedStudent.lastActivity)}
                        </p>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Body>
                    <h6>الكورسات المشترك فيها</h6>
                    <ListGroup variant="flush">
                      {Object.values(selectedStudent.progress).map((progress: any, index: number) => (
                        <ListGroup.Item
                          key={`student-progress-${progress.courseId || index}`}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div className="d-flex align-items-center">
                            <div>
                              <h6 className="mb-0">{progress.courseName}</h6>
                              <small>
                                {progress.completedLessons} من {progress.totalLessons} درس مكتمل
                              </small>
                            </div>
                            <div className="text-end">
                              <Badge bg={progress.progress >= 70 ? 'success' : 'warning'}>
                                {Math.round(progress.progress || 0)}%
                              </Badge>
                            </div>
                          </div>
                          <ProgressBar
                            now={progress.progress || 0}
                            className="mt-2"
                            style={{ height: '5px' }}
                          />
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Body>
                    <h6>ملاحظات</h6>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={studentNote}
                      onChange={e => setStudentNote(e.target.value)}
                      placeholder="أضف ملاحظاتك عن الطالب هنا..."
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowStudentModal(false)}>
          إغلاق
        </Button>
        <Button variant="primary" onClick={handleSaveNote}>
          حفظ الملاحظات
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const handleManageCodes = (courseId: string) => {
    setSelectedCourseForCodes(courseId);
    setShowCodesModal(true);
  };

  const renderCoursesTab = () => (
    <>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">الكورسات الخاصة بك</h5>
              <Button variant="primary" size="sm" onClick={() => navigate('/courses/new')}>
                <FaPlus className="me-1" /> إضافة كورس جديد
              </Button>
            </Card.Header>
            <Card.Body>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>الكورس</th>
                    <th>عدد الطلاب</th>
                    <th>متوسط التقدم</th>
                    <th>عدد الدروس</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.studentsPerCourse && Object.keys(stats?.studentsPerCourse).map(courseId => (
                    <tr key={courseId}>
                      <td>
                        {stats?.recentEnrollments.find(e => e.courseId === courseId)?.courseName || courseId}
                      </td>
                      <td>{stats?.studentsPerCourse[courseId]}</td>
                      <td>
                        <ProgressBar 
                          now={stats?.progressPerCourse?.[courseId] || 0} 
                          label={`${Math.round(stats?.progressPerCourse?.[courseId] || 0)}%`}
                          variant={getProgressVariant(stats?.progressPerCourse?.[courseId] || 0)}
                        />
                      </td>
                      <td>
                        {stats?.recentEnrollments.find(e => e.courseId === courseId)?.completedLessons?.length || 0}
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-1"
                          onClick={() => navigate(`/courses/${courseId}`)}
                        >
                          <FaEye className="me-1" />
                          عرض
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          className="me-1"
                          onClick={() => navigate(`/courses/${courseId}/edit`)}
                        >
                          <FaEdit className="me-1" />
                          تعديل
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          onClick={() => handleManageCodes(courseId)}
                        >
                          <FaKey className="me-1" />
                          الأكواد
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">أكثر الدروس مشاهدة</h5>
            </Card.Header>
            <Card.Body>
              {lessonEngagement.length > 0 ? (
                <ListGroup variant="flush">
                  {lessonEngagement
                    .sort((a, b) => b.watchCount - a.watchCount)
                    .slice(0, 5)
                    .map(lesson => (
                      <ListGroup.Item key={lesson.lessonId}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-0">{lesson.lessonTitle}</h6>
                            <small>{lesson.watchCount} مشاهدة</small>
                          </div>
                          <Badge bg="info">{Math.round(lesson.averageProgress || 0)}%</Badge>
                        </div>
                      </ListGroup.Item>
                    ))}
                </ListGroup>
              ) : (
                <p className="text-center text-muted">اختر كورس لعرض إحصائيات الدروس</p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">الدروس ذات أعلى معدل إكمال</h5>
            </Card.Header>
            <Card.Body>
              {lessonEngagement.length > 0 ? (
                <ListGroup variant="flush">
                  {lessonEngagement
                    .sort((a, b) => b.completionRate - a.completionRate)
                    .slice(0, 5)
                    .map(lesson => (
                      <ListGroup.Item key={lesson.lessonId}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-0">{lesson.lessonTitle}</h6>
                            <small>{Math.round(lesson.completionRate * 100)}% معدل الإكمال</small>
                          </div>
                          <Badge bg="success">{formatTime(lesson.averageWatchTime || 0)}</Badge>
                        </div>
                      </ListGroup.Item>
                    ))}
                </ListGroup>
              ) : (
                <p className="text-center text-muted">اختر كورس لعرض إحصائيات الدروس</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderStudentsTab = () => {
    const filteredStudents = students.filter(student => 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <>
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">جميع الطلاب</h5>
              </Card.Header>
              <Card.Body>
                {loadingStudents ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                  </div>
                ) : filteredStudents.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>الطالب</th>
                        <th>البريد الإلكتروني</th>
                        <th>عدد الكورسات</th>
                        <th>آخر نشاط</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map(student => (
                        <tr key={student.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {student.photoURL ? (
                                <img
                                  src={student.photoURL}
                                  alt={student.name}
                                  className="rounded-circle me-2"
                                  width="30"
                                  height="30"
                                />
                              ) : (
                                <div className="avatar-placeholder-small me-2">
                                  {student.name[0]}
                                </div>
                              )}
                              {student.name}
                            </div>
                          </td>
                          <td>{student.email}</td>
                          <td>{student.courses?.length || 0}</td>
                          <td>
                            {student.updatedAt ? formatDate(student.updatedAt) : 'غير متوفر'}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleStudentClick(student.id)}
                            >
                              <FaEye /> عرض التفاصيل
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-center text-muted">لا يوجد طلاب مسجلين في كورساتك</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">الطلاب الأكثر نشاطاً</h5>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  {stats?.topPerformingStudents.map(student => (
                    <ListGroup.Item
                      key={student.userId}
                      action
                      onClick={() => handleStudentClick(student.userId)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        {student.userPhoto ? (
                          <img
                            src={student.userPhoto}
                            alt={student.userName}
                            className="rounded-circle me-2"
                            width="40"
                            height="40"
                          />
                        ) : (
                          <div className="avatar-placeholder me-2">{student.userName[0]}</div>
                        )}
                        <div>
                          <h6 className="mb-0">{student.userName}</h6>
                          <small>{student.courseName}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div>
                          <Badge bg="success">{Math.round(student.progress)}%</Badge>
                        </div>
                        <small>{formatTime(student.watchTime)}</small>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">الطلاب الذين يحتاجون للمتابعة</h5>
                <Badge bg="warning" text="dark">يحتاجون للمتابعة</Badge>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  {stats?.lowEngagementStudents.map(student => (
                    <ListGroup.Item
                      key={student.userId}
                      action
                      onClick={() => handleStudentClick(student.userId)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        {student.userPhoto ? (
                          <img
                            src={student.userPhoto}
                            alt={student.userName}
                            className="rounded-circle me-2"
                            width="40"
                            height="40"
                          />
                        ) : (
                          <div className="avatar-placeholder me-2">{student.userName[0]}</div>
                        )}
                        <div>
                          <h6 className="mb-0">{student.userName}</h6>
                          <small>{student.courseName}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleStudentClick(student.userId)}
                        >
                          <FaEye /> عرض التفاصيل
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderAnalyticsTab = () => (
    <>
      <Row className="mb-4">
        <Col md={3}>
          {renderStatCard(
            <FaClock />,
            'وقت المشاهدة',
            formatTime(stats?.totalWatchTime || 0),
            'bg-info'
          )}
        </Col>
        <Col md={3}>
          {renderStatCard(
            <FaChartLine />,
            'متوسط التقدم',
            `${Math.round(stats?.averageProgress || 0)}%`,
            'bg-success'
          )}
        </Col>
        <Col md={3}>
          {renderStatCard(
            <FaStar />,
            'متوسط المشاركة',
            `${Math.round((stats?.averageEngagement || 0) * 10)}/100`,
            'bg-warning'
          )}
        </Col>
        <Col md={3}>
          {renderStatCard(
            <FaUserGraduate />,
            'الطلاب النشطين',
            `${stats?.activeStudents || 0}/${stats?.totalStudents || 0}`,
            'bg-primary'
          )}
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">توزيع الطلاب على الكورسات</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>الكورس</th>
                    <th>عدد الطلاب</th>
                    <th>متوسط التقدم</th>
                    <th>نسبة الطلاب</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.studentsPerCourse && Object.keys(stats?.studentsPerCourse).map(courseId => (
                    <tr key={courseId}>
                      <td>
                        {stats?.recentEnrollments.find(e => e.courseId === courseId)?.courseName || courseId}
                      </td>
                      <td>{stats?.studentsPerCourse[courseId]}</td>
                      <td>
                        <ProgressBar 
                          now={stats?.progressPerCourse?.[courseId] || 0} 
                          label={`${Math.round(stats?.progressPerCourse?.[courseId] || 0)}%`}
                          variant={getProgressVariant(stats?.progressPerCourse?.[courseId] || 0)}
                        />
                      </td>
                      <td>
                        {stats?.totalStudents > 0 
                          ? `${Math.round((stats?.studentsPerCourse[courseId] / stats?.totalStudents) * 100)}%` 
                          : '0%'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">الطلاب الذين يحتاجون للمتابعة</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {stats?.lowEngagementStudents.map(student => (
                  <ListGroup.Item
                    key={student.userId}
                    action
                    onClick={() => handleStudentClick(student.userId)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center">
                      <div>
                        <h6 className="mb-0">{student.userName}</h6>
                        <small>{student.courseName}</small>
                      </div>
                    </div>
                    <div>
                      <Badge bg="danger">
                        <FaExclamationTriangle className="me-1" />
                        {Math.round(student.engagementScore || 0) * 10}/100
                      </Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">آخر الاشتراكات</h5>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {stats?.recentEnrollments.map(enrollment => (
                  <ListGroup.Item
                    key={enrollment.id}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <h6 className="mb-0">{(enrollment as any).studentName || 'طالب'}</h6>
                      <small>{enrollment.courseName}</small>
                    </div>
                    <div>
                      {formatDate(enrollment.enrollmentDate)}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderCodesTab = () => (
    <Row>
      <Col xs={12}>
        <Alert variant="info">
          اختر كورس من قائمة الكورسات لإدارة أكواد التفعيل الخاصة به
        </Alert>
      </Col>
    </Row>
  );

  const renderCodesModal = () => (
    <Modal
      show={showCodesModal}
      onHide={() => setShowCodesModal(false)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaKey className="me-2" />
          إدارة أكواد التفعيل
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedCourseForCodes && (
          <>
            <Row>
              <Col>
                <Alert variant="info" className="d-flex justify-content-between align-items-center">
                  <span>أكواد التفعيل للكورس: <strong>{courses.find(c => c.id === selectedCourseForCodes)?.title}</strong></span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      // العثور على مرجع لمكون CourseCodesManager وفتح مودال إنشاء الأكواد
                      const courseCodesManagerElement = document.querySelector('.course-codes-manager button');
                      if (courseCodesManagerElement) {
                        (courseCodesManagerElement as HTMLButtonElement).click();
                      }
                    }}
                  >
                    <FaPlus className="me-1" />
                    إنشاء أكواد جديدة
                  </Button>
                </Alert>
              </Col>
            </Row>
            
            <Tabs defaultActiveKey="list" id="codes-tabs" className="mb-3">
              <Tab eventKey="list" title="قائمة الأكواد">
                <CourseCodesManager
                  course={courses.find(c => c.id === selectedCourseForCodes) || {
                    id: selectedCourseForCodes,
                    title: 'الكورس',
                    teacherId: currentUser?.uid || '',
                    teacherName: currentUser?.displayName || 'معلم',
                    description: '',
                    imageUrl: '',
                    price: 0,
                    isPublished: false,
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                  }}
                />
              </Tab>
              <Tab eventKey="reports" title="تقارير الاستخدام">
                <CourseCodesReports
                  course={courses.find(c => c.id === selectedCourseForCodes) || {
                    id: selectedCourseForCodes,
                    title: 'الكورس',
                    teacherId: currentUser?.uid || '',
                    teacherName: currentUser?.displayName || 'معلم',
                    description: '',
                    imageUrl: '',
                    price: 0,
                    isPublished: false,
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                  }}
                />
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowCodesModal(false)}>
          إغلاق
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderQuickLinks = () => (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">روابط سريعة</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xs={6} md={3} className="mb-3">
            <Button
              variant="outline-primary"
              className="w-100 py-3 d-flex flex-column align-items-center justify-content-center"
              onClick={() => navigate('/teacher/courses')}
            >
              <FaBook className="mb-2" size={24} />
              <span>الكورسات</span>
            </Button>
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <Button
              variant="outline-primary"
              className="w-100 py-3 d-flex flex-column align-items-center justify-content-center"
              onClick={() => navigate('/teacher/students')}
            >
              <FaUsers className="mb-2" size={24} />
              <span>الطلاب</span>
            </Button>
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <Button
              variant="outline-primary"
              className="w-100 py-3 d-flex flex-column align-items-center justify-content-center"
              onClick={() => navigate('/teacher/codes-manager')}
            >
              <FaKey className="mb-2" size={24} />
              <span>أكواد التفعيل</span>
            </Button>
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <Button
              variant="outline-primary"
              className="w-100 py-3 d-flex flex-column align-items-center justify-content-center"
              onClick={() => navigate('/teacher/profile')}
            >
              <FaUserGraduate className="mb-2" size={24} />
              <span>الملف الشخصي</span>
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderCourseActivityChart = () => {
    if (!stats || !stats.progressPerCourse) return null;

    const chartData = Object.keys(stats.progressPerCourse).map(courseId => {
      const course = courses.find(c => c.id === courseId);
      return {
        name: course?.title || 'غير معروف',
        students: stats.studentsPerCourse[courseId] || 0,
        progress: Math.round(stats.progressPerCourse[courseId] || 0),
        fill: `#${Math.floor(Math.random() * 16777215).toString(16)}` // لون عشوائي
      };
    });

    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">نصيب الكورسات</h5>
        </Card.Header>
        <Card.Body>
          <div className="chart-placeholder" />
        </Card.Body>
      </Card>
    );
  };

  const renderLessonEngagementChart = () => {
    if (!lessonEngagement || lessonEngagement.length === 0) return null;

    const sortedLessons = [...lessonEngagement].sort((a, b) => b.watchCount - a.watchCount);
    const topLessons = sortedLessons.slice(0, 5); // أعلى 5 دروس مشاهدة

    const chartData = topLessons.map(lesson => ({
      name: lesson.lessonTitle,
      watchCount: lesson.watchCount,
      completionRate: Math.round(lesson.completionRate * 100),
      averageProgress: Math.round(lesson.averageProgress || 0)
    }));

    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">أعلى الدروس مشاهدة</h5>
        </Card.Header>
        <Card.Body>
          <div className="chart-placeholder" />
        </Card.Body>
      </Card>
    );
  };

  const renderStudentDistributionChart = () => {
    if (!stats || !stats.studentsPerCourse) return null;

    const chartData: ChartDataEntry[] = Object.keys(stats.studentsPerCourse)
      .map(courseId => {
        const course = courses.find(c => c.id === courseId);
        return {
          name: course?.title || 'غير معروف',
          value: stats.studentsPerCourse[courseId] || 0
        };
      })
      .filter(item => item.value > 0);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">توزيع الطلاب على الكورسات</h5>
        </Card.Header>
        <Card.Body>
          <div className="chart-placeholder" />
        </Card.Body>
      </Card>
    );
  };

  const getProgressVariant = (progress: number): string => {
    if (progress > 75) return 'success';
    if (progress > 50) return 'info';
    if (progress > 25) return 'warning';
    return 'danger';
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        
        // تحميل الكورسات
        const teacherCourses = await getCoursesByTeacherId(currentUser.uid);
        setCourses(teacherCourses);

        // تحميل الإحصائيات
        const dashboardStats = await getTeacherDashboardStats(currentUser.uid);
        setStats(dashboardStats);

        // تحميل مشاركة الدروس للكورس الأول
        if (teacherCourses.length > 0) {
          const engagement = await getLessonEngagement(teacherCourses[0].id);
          setLessonEngagement(engagement);
          setSelectedCourse(teacherCourses[0].id);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setError('حدث خطأ أثناء تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [currentUser]);

  useEffect(() => {
    const loadLessonEngagement = async () => {
      if (!selectedCourse) return;

      try {
        const engagement = await getLessonEngagement(selectedCourse);
        setLessonEngagement(engagement);
      } catch (error) {
        console.error('Error loading lesson engagement:', error);
      }
    };

    loadLessonEngagement();
  }, [selectedCourse]);

  useEffect(() => {
    const loadStudents = async () => {
      if (!currentUser) return;
      setLoadingStudents(true);
      try {
        const studentsData = await getTeacherStudents(currentUser.uid);
        setStudents(studentsData);
      } catch (err) {
        console.error('Error loading students:', err);
        setError('حدث خطأ أثناء تحميل بيانات الطلاب');
      } finally {
        setLoadingStudents(false);
      }
    };

    loadStudents();
  }, [currentUser]);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      <h2 className="mb-4">لوحة التحكم للمعلم</h2>
      {renderQuickLinks()}
      <Tabs
        defaultActiveKey="overview"
        id="teacher-dashboard-tabs"
        className="mb-4"
      >
        <Tab eventKey="overview" title="نظرة عامة">
          {renderOverview()}
        </Tab>
        
        <Tab eventKey="courses" title="الكورسات">
          <Row className="g-4">
            {courses.map(course => (
              <Col key={course.id} xs={12}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className="mb-1">{course.title}</h5>
                        <p className="text-muted mb-0">{course.description}</p>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/courses/${course.id}`)}
                      >
                        <FaEye className="me-1" />
                        عرض الكورس
                      </Button>
                    </div>
                    <CourseCodesManager course={course} />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Tab>
        
        <Tab eventKey="students" title="الطلاب">
          {renderStudentsTab()}
        </Tab>
        
        <Tab eventKey="codes" title={<><FaKey className="me-1" />أكواد التفعيل</>}>
          {renderCodesTab()}
        </Tab>
      </Tabs>
      {renderStudentModal()}
      {renderCodesModal()}
      <Row className="mt-4">
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">تقدم الطلاب</h5>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
              {stats && (
                <Line
                  data={{
                    labels: Object.keys(stats.progressPerCourse || {}),
                    datasets: [{
                      label: 'تقدم الطلاب',
                      data: Object.values(stats.progressPerCourse || {}),
                      borderColor: 'rgb(255, 99, 132)',
                      backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    }]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">توزيع الطلاب</h5>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
              {stats && (
                <Bar
                  data={{
                    labels: Object.keys(stats.studentsPerCourse || {}),
                    datasets: [{
                      label: 'توزيع الطلاب',
                      data: Object.values(stats.studentsPerCourse || {}),
                      borderColor: 'rgb(255, 99, 132)',
                      backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    }]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">نشاط المشاهدة</h5>
            </Card.Header>
            <Card.Body style={{ height: '300px' }}>
              {lessonEngagement && lessonEngagement.length > 0 ? (
                <Line
                  data={{
                    labels: lessonEngagement.map(lesson => lesson.lessonTitle),
                    datasets: [{
                      label: 'مشاهدات',
                      data: lessonEngagement.map(lesson => lesson.watchCount),
                      borderColor: 'rgb(255, 99, 132)',
                      backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    }, {
                      label: 'إكمال',
                      data: lessonEngagement.map(lesson => Math.round(lesson.completionRate * 100)),
                      borderColor: 'rgb(54, 162, 235)',
                      backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    }]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              ) : (
                <div className="text-center p-5">
                  <p className="text-muted">لا توجد بيانات مشاهدة متاحة</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TeacherDashboard;